import {
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Fade,
  Skeleton,
  useTheme,
  useMediaQuery,
  styled,
  tableCellClasses,
} from '@mui/material';
import React, { useEffect, useState, useContext } from 'react';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  RemoveRedEyeRounded,
  DownloadRounded,
  Add,
  TableView,
} from '@mui/icons-material';
import NoTableData from './NoTableData';
import { PuffLoader } from 'react-spinners';
import image from '../../assets/images/deleteicon.png';
import ShowComponent from '../ShowComponent';
import { LoadingButton } from '@mui/lab';
import { ROWS_PER_PAGE } from '../../utils/utilities';
import CircularProgress from '@mui/material/CircularProgress';
import CustomInput from '../SecondaryForms/CustomInput';
import { toast } from 'react-toastify';
import ThemeModeContext from '../../context/ThemeModeContext';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: { xs: 11, sm: 12 },
    fontWeight: 500,
    padding: { xs: '4px 6px', sm: '6px 8px' },
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
    }`,
    minHeight: { xs: '32px', sm: '36px' },
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.head}`]: {
    fontSize: { xs: 11, sm: 12 },
    fontWeight: 700,
    padding: { xs: '6px 6px', sm: '8px 8px' },
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.87)',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    backdropFilter: 'blur(8px)',
    boxShadow:
      theme.palette.mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
    minHeight: { xs: '36px', sm: '40px' },
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90vw', sm: 400 },
  maxWidth: 500,
  bgcolor: 'background.paper',
  borderRadius: 4,
  boxShadow: 24,
  p: 3,
  border: 'none',
  outline: 'none',
};

export function getDateInDDMMYYYY(utcDateString) {
  // Create a Date object from the UTC string
  const date = new Date(utcDateString);

  // Extract day, month, and year
  const day = String(date.getUTCDate()).padStart(2, '0'); // Ensure 2 digits
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-based, so +1
  const year = date.getUTCFullYear();

  // Return formatted date
  return `${day}/${month}/${year}`;
}

const getDataValue = (data, k) => {
  if (typeof data[k.key] === 'boolean') {
    return data[k.key] === true ? 'Yes' : 'No';
  }
  if (k.path) {
    if (Object.keys(data[k.path] || {}).length > 0) {
      return data[k.path][k.key] || 'NA';
    }
  }

  if (k.isDate) {
    return data[k.key]
      ? `${getDateInDDMMYYYY(data[k.key])} ${
          new Date(data[k.key])
            .toLocaleString('en-IN', {
              timeZone: 'Asia/Kolkata',
            })
            .split(' ')[1]
        }`
      : 'NA';
  } else {
    return data[k.key] || 'NA';
  }
};

// const getDataValue = (data, k) => {
//   if (typeof data[k.key] === "boolean") {
//     return data[k.key] === true ? "Yes" : "No";
//   }
//   if (k.path) {
//     if (Object.keys(data[k.path] || {}).length > 0) {
//       return data[k.path][k.key] || "NA";
//     }
//   }

//   if (k.isDate) {
//     if (!data[k.key]) return "NA";

//     const originalDate = new Date(data[k.key]);
//     const utcDay = originalDate.getUTCDate(); // Day in UTC
//     const istDate = new Date(
//       originalDate.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
//     );
//     const istDay = istDate.getDate(); // Day in IST

//     if (utcDay !== istDay) {
//       // If the day changes due to timezone shift, return the original UTC date-time as-is
//       return originalDate.toISOString().replace("T", " ").slice(0, 19); // e.g., "2025-02-07 20:11:03"
//     } else {
//       // If the day doesn't change, format it to IST
//       return originalDate.toLocaleString("en-IN", {
//         timeZone: "Asia/Kolkata",
//       });
//     }
//   } else {
//     return data[k.key] || "NA";
//   }
// };

export default function CustomTable({
  bodyData = [],
  bodyDataModal = '',
  tableKeys = [],
  actions = ['edit', 'delete', 'view', 'switch', 'unbundle', 'download'],
  onEditClick = () => {},
  onDeleteClick = () => {},
  onViewClick = () => {},
  onUnBundleClick = () => {},
  onToggleSwitch = () => {},
  onDownload = () => {},
  toggleStatus = false,
  isDataReady = true,
  module = '',
  formFields = [],
  deleteLoading,
  downloadLoading,
  disableDeleteFunction = () => {},
  deleteCanBeDisabled = false,
}) {
  const { isDarkMode } = useContext(ThemeModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [reasonForDelete, setReasonForDelete] = useState('');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleting, setDeleteing] = useState(false);

  const handleDeleteClick = data => {
    setSelectedItem(data);
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setDeleteing(true);
      if (!reasonForDelete) {
        toast.error('Reason for deletion is required field');
        setDeleteing(false);
        return;
      }
      await onDeleteClick(selectedItem._id, reasonForDelete);
      setOpen(false);
      setReasonForDelete('');
      setDeleteing(false);
    } catch (error) {
      console.log(error);
      setDeleteing(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setReasonForDelete('');
  };

  useEffect(() => {
    setPage(0);
  }, [bodyData]);

  return (
    <>
      <Fade in timeout={500}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: { xs: 2, sm: 2.5 },
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(12px)',
            transition: 'all 0.2s ease',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Header Section */}
            <Box
              sx={{
                p: { xs: 1, sm: 1.5 },
                borderBottom: `1px solid ${
                  isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                }`,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.primary.main}06 100%)`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                <Box
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
                  }}
                >
                  <TableView sx={{ fontSize: { xs: 16, sm: 18 }, color: 'white' }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      color: theme.palette.primary.main,
                      mb: 0.25,
                    }}
                  >
                    Data Table
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, display: 'block' }}
                  >
                    {bodyDataModal || 'Manage and view your data'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Table Content */}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650, width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell
                      align="center"
                      sx={{
                        minWidth: { xs: '50px', sm: '60px' },
                        maxWidth: { xs: '50px', sm: '60px' },
                        width: { xs: '50px', sm: '60px' },
                      }}
                    >
                      S.No
                    </StyledTableCell>
                    {tableKeys.map((r, i) => (
                      <StyledTableCell
                        key={i}
                        align="center"
                        sx={{
                          minWidth: { xs: '120px', sm: '150px' },
                          maxWidth: { xs: '200px', sm: '250px' },
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        {r.name}
                      </StyledTableCell>
                    ))}
                    {formFields?.length
                      ? formFields?.map((f, index) => (
                          <StyledTableCell
                            key={f.inputLabel + index.toString()}
                            align="center"
                            sx={{
                              minWidth: { xs: '120px', sm: '150px' },
                              maxWidth: { xs: '200px', sm: '250px' },
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {f.inputLabel}
                          </StyledTableCell>
                        ))
                      : null}
                    {actions.length ? (
                      <StyledTableCell
                        align="center"
                        sx={{
                          minWidth: { xs: '140px', sm: '160px' },
                          maxWidth: { xs: '140px', sm: '160px' },
                          width: { xs: '140px', sm: '160px' },
                        }}
                      >
                        Actions
                      </StyledTableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bodyData.length && isDataReady
                    ? bodyData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((data, index) => (
                          <StyledTableRow key={data._id}>
                            <StyledTableCell
                              align="center"
                              sx={{
                                minWidth: { xs: '50px', sm: '60px' },
                                maxWidth: { xs: '50px', sm: '60px' },
                                width: { xs: '50px', sm: '60px' },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: theme.palette.primary.main,
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {page * rowsPerPage + index + 1}
                              </Box>
                            </StyledTableCell>
                            {tableKeys.map((k, i) => (
                              <StyledTableCell
                                key={i}
                                align="center"
                                sx={{
                                  minWidth: { xs: '120px', sm: '150px' },
                                  maxWidth: { xs: '200px', sm: '250px' },
                                  wordBreak: 'break-word',
                                  whiteSpace: 'normal',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                  title={getDataValue(data, k)}
                                >
                                  {getDataValue(data, k)}
                                </Typography>
                              </StyledTableCell>
                            ))}

                            {formFields?.length
                              ? formFields?.map((f, index) => (
                                  <StyledTableCell
                                    key={f.inputLabel + index.toString()}
                                    align="center"
                                    sx={{
                                      minWidth: { xs: '120px', sm: '150px' },
                                      maxWidth: { xs: '200px', sm: '250px' },
                                      wordBreak: 'break-word',
                                      whiteSpace: 'normal',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                      title={
                                        data.meta?.find(fi => fi.inputLabel === f.inputLabel)
                                          ?.value || ''
                                      }
                                    >
                                      {data.meta?.find(fi => fi.inputLabel === f.inputLabel)
                                        ?.value || ''}
                                    </Typography>
                                  </StyledTableCell>
                                ))
                              : null}
                            <StyledTableCell
                              align="center"
                              sx={{
                                minWidth: { xs: '140px', sm: '160px' },
                                maxWidth: { xs: '140px', sm: '160px' },
                                width: { xs: '140px', sm: '160px' },
                                padding: { xs: '4px 6px', sm: '6px 8px' },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  gap: { xs: 0.25, sm: 0.5 },
                                  flexWrap: 'nowrap',
                                  overflow: 'hidden',
                                }}
                              >
                                {actions.includes('edit') && (
                                  <ShowComponent module={module} action={'update'}>
                                    <Tooltip title="Edit" arrow>
                                      <IconButton
                                        onClick={() => onEditClick(data)}
                                        size="small"
                                        sx={{
                                          width: { xs: 24, sm: 28 },
                                          height: { xs: 24, sm: 28 },
                                          minWidth: { xs: 24, sm: 28 },
                                          padding: 0,
                                          bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(0,0,0,0.05)',
                                          '&:hover': {
                                            bgcolor: isDarkMode
                                              ? 'rgba(255,255,255,0.15)'
                                              : 'rgba(0,0,0,0.08)',
                                            transform: 'translateY(-1px)',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        <EditIcon
                                          sx={{
                                            fontSize: { xs: 12, sm: 14 },
                                            color: theme.palette.primary.main,
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </ShowComponent>
                                )}

                                {actions.includes('view') && (
                                  <ShowComponent module={module} action={'view'}>
                                    <Tooltip title="View Details" arrow>
                                      <IconButton
                                        onClick={() => onViewClick(data)}
                                        size="small"
                                        sx={{
                                          width: { xs: 24, sm: 28 },
                                          height: { xs: 24, sm: 28 },
                                          minWidth: { xs: 24, sm: 28 },
                                          padding: 0,
                                          bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(0,0,0,0.05)',
                                          '&:hover': {
                                            bgcolor: isDarkMode
                                              ? 'rgba(255,255,255,0.15)'
                                              : 'rgba(0,0,0,0.08)',
                                            transform: 'translateY(-1px)',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        <RemoveRedEyeRounded
                                          sx={{
                                            fontSize: { xs: 12, sm: 14 },
                                            color: theme.palette.info.main,
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </ShowComponent>
                                )}

                                {actions.includes('delete') && (
                                  <ShowComponent module={module} action={'delete'}>
                                    <Tooltip title="Delete" arrow>
                                      <IconButton
                                        disabled={
                                          deleteCanBeDisabled && !disableDeleteFunction(data)
                                        }
                                        onClick={() => handleDeleteClick(data)}
                                        size="small"
                                        sx={{
                                          width: { xs: 24, sm: 28 },
                                          height: { xs: 24, sm: 28 },
                                          minWidth: { xs: 24, sm: 28 },
                                          padding: 0,
                                          bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(0,0,0,0.05)',
                                          '&:hover': {
                                            bgcolor: isDarkMode
                                              ? 'rgba(255,255,255,0.15)'
                                              : 'rgba(0,0,0,0.08)',
                                            transform: 'translateY(-1px)',
                                          },
                                          '&:disabled': {
                                            bgcolor: isDarkMode
                                              ? 'rgba(255,255,255,0.05)'
                                              : 'rgba(0,0,0,0.02)',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        <DeleteIcon
                                          sx={{
                                            fontSize: { xs: 12, sm: 14 },
                                            color:
                                              deleteCanBeDisabled && !disableDeleteFunction(data)
                                                ? theme.palette.action.disabled
                                                : theme.palette.error.main,
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </ShowComponent>
                                )}

                                {actions.includes('download') && (
                                  <ShowComponent module={module} action={'view'}>
                                    <Tooltip title="Download" arrow>
                                      <IconButton
                                        onClick={() => onDownload(data)}
                                        disabled={!!downloadLoading[data._id]}
                                        size="small"
                                        sx={{
                                          width: { xs: 24, sm: 28 },
                                          height: { xs: 24, sm: 28 },
                                          minWidth: { xs: 24, sm: 28 },
                                          padding: 0,
                                          bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(0,0,0,0.05)',
                                          '&:hover': {
                                            bgcolor: isDarkMode
                                              ? 'rgba(255,255,255,0.15)'
                                              : 'rgba(0,0,0,0.08)',
                                            transform: 'translateY(-1px)',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        {downloadLoading[data._id] ? (
                                          <CircularProgress
                                            size={{ xs: 12, sm: 14 }}
                                            sx={{ color: theme.palette.primary.main }}
                                          />
                                        ) : (
                                          <DownloadRounded
                                            sx={{
                                              fontSize: { xs: 12, sm: 14 },
                                              color: theme.palette.success.main,
                                            }}
                                          />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </ShowComponent>
                                )}

                                {actions.includes('unbundle') && (
                                  <ShowComponent module={module} action={'delete'}>
                                    <Tooltip title="Unbundle" arrow>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => onUnBundleClick(data)}
                                        sx={{
                                          borderRadius: 1.5,
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                          px: { xs: 1, sm: 1.5 },
                                          py: { xs: 0.25, sm: 0.5 },
                                          minHeight: { xs: 24, sm: 28 },
                                          bgcolor: theme.palette.warning.main,
                                          '&:hover': {
                                            bgcolor: theme.palette.warning.dark,
                                            transform: 'translateY(-1px)',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        UnBundle
                                      </Button>
                                    </Tooltip>
                                  </ShowComponent>
                                )}

                                {actions.includes('switch') && (
                                  <ShowComponent module={module} action={'update'}>
                                    <Tooltip title="Toggle Active Status" arrow>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Switch
                                          checked={data.active || false}
                                          onChange={() => onToggleSwitch(data)}
                                          size="small"
                                          sx={{
                                            '& .MuiSwitch-thumb': {
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            },
                                            '& .MuiSwitch-track': {
                                              borderRadius: 3,
                                            },
                                          }}
                                        />
                                      </Box>
                                    </Tooltip>
                                  </ShowComponent>
                                )}
                              </Box>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))
                    : null}
                </TableBody>
              </Table>

              {/* Loading State */}
              {!isDataReady && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    gap: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {[...Array(3)].map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="rectangular"
                        width={200}
                        height={40}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {[...Array(3)].map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="rectangular"
                        width={200}
                        height={40}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {[...Array(3)].map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="rectangular"
                        width={200}
                        height={40}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* No Data State */}
              {isDataReady && <NoTableData dataPresent={bodyData.length} title={bodyDataModal} />}

              {/* Enhanced Pagination */}
              <Box
                sx={{
                  borderTop: `1px solid ${
                    isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                  }`,
                  background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                }}
              >
                <TablePagination
                  component="div"
                  count={bodyData ? (bodyData.length ? bodyData.length : 0) : 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={ROWS_PER_PAGE}
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      minHeight: { xs: '48px', sm: '52px' },
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: theme.palette.text.primary,
                    },
                    '& .MuiTablePagination-select': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        width: { xs: 32, sm: 36 },
                        height: { xs: 32, sm: 36 },
                        '& .MuiSvgIcon-root': {
                          fontSize: { xs: 16, sm: 18 },
                        },
                      },
                    },
                    '& .MuiTablePagination-select': {
                      borderRadius: 2,
                      border: `1px solid ${
                        isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'
                      }`,
                      px: 1,
                      py: 0.5,
                    },
                    '& .MuiTablePagination-actions button': {
                      borderRadius: 2,
                      border: `1px solid ${
                        isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'
                      }`,
                      mx: 0.5,
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                      },
                    },
                  }}
                />
              </Box>
            </TableContainer>
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Delete Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Fade in={open}>
          <Card sx={style}>
            <CardContent sx={{ textAlign: 'center' }}>
              {/* Icon Section */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
              </Box>

              {/* Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.error.main,
                  mb: 2,
                }}
              >
                Delete Confirmation
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Proceeding with the deletion of this data may result in information inconsistency
                across other documents and is{' '}
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, color: theme.palette.error.main }}
                >
                  irreversible
                </Typography>
                . Are you certain you wish to delete this item?
              </Typography>

              {/* Reason Input */}
              <Box sx={{ mb: 3 }}>
                <CustomInput
                  name="reason"
                  value={reasonForDelete}
                  onChange={e => setReasonForDelete(e.target.value)}
                  label="Reason For Deletion"
                  multiline
                  required={true}
                  rows={3}
                  fullWidth
                />
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleClose}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      border: `1px solid ${
                        isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
                      }`,
                    },
                  }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  color="error"
                  onClick={handleSubmit}
                  loading={deleting}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    bgcolor: theme.palette.error.main,
                    '&:hover': {
                      bgcolor: theme.palette.error.dark,
                    },
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </LoadingButton>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </>
  );
}

// import {
//   Box,
//   Button,
//   IconButton,
//   Modal,
//   Paper,
//   Switch,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TablePagination,
//   TableRow,
//   Tooltip,
//   Typography,
// } from "@mui/material";
// import React, { useEffect, useState } from "react";
// import DeleteIcon from "@mui/icons-material/Delete";
// import EditIcon from "@mui/icons-material/Edit";
// import NoTableData from "./NoTableData";
// import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
// import { PuffLoader } from "react-spinners";
// import image from "../../assets/images/deleteicon.png";
// import ShowComponent from "../ShowComponent";
// import { LoadingButton } from "@mui/lab";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import { ROWS_PER_PAGE } from "../../utils/utilities";
// import CircularProgress from "@mui/material/CircularProgress";
// import CustomInput from "../SecondaryForms/CustomInput";
// import { toast } from "react-toastify";

// const style = {
//   position: "absolute",
//   top: "50%",
//   left: "50%",
//   transform: "translate(-50%, -50%)",
//   width: 350,
//   bgcolor: "background.paper",
//   borderRadius: "10px",
//   boxShadow: 24,
//   p: 2,
// };

// const getDataValue = (data, k) => {
//   if (typeof data[k.key] === "boolean") {
//     return data[k.key] === true ? "Yes" : "No";
//   }
//   if (k.path) {
//     if (Object.keys(data[k.path] || {}).length > 0) {
//       return data[k.path][k.key] || "NA";
//     }
//   }
//   if (k.isDate) {
//     return data[k.key]
//       ? new Date(data[k.key]).toLocaleString("en-IN", {
//           timeZone: "Asia/Kolkata",
//         })
//       : "NA";
//   } else {
//     return data[k.key] || "NA";
//   }
// };

// export default function CustomTable({
//   bodyData = [],
//   bodyDataModal = "",
//   tableKeys = [],
//   actions = ["edit", "delete", "view", "switch", "unbundle", "download"],
//   onEditClick = () => {},
//   onDeleteClick = () => {},
//   onViewClick = () => {},
//   onUnBundleClick = () => {},
//   onToggleSwitch = () => {},
//   onDownload = () => {},
//   toggleStatus = false,
//   isDataReady = true,
//   module = "",
//   formFields = [],
//   deleteLoading,
//   downloadLoading,
//   disableDeleteFunction = () => {},
//   deleteCanBeDisabled = false,
// }) {
//   const [page, setPage] = React.useState(0);
//   const [rowsPerPage, setRowsPerPage] = React.useState(10);
//   const [reasonForDelete, setReasonForDelete] = useState("");
//   const [open, setOpen] = useState(false);
//   const [selectedItem, setSelectedItem] = useState(null);
//   const [deleting, setDeleteing] = useState(false);

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage);
//   };

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10));
//     setPage(0);
//   };

//   const handleDeleteClick = (data) => {
//     setSelectedItem(data);
//     setOpen(true);
//   };

//   const handleSubmit = async () => {
//     try {
//       setDeleteing(true);
//       if (!reasonForDelete) {
//         toast.error("Reason for deletion is required field");
//         setDeleteing(false);
//         return;
//       }
//       await onDeleteClick(selectedItem._id, reasonForDelete);
//       setOpen(false);
//       setReasonForDelete("");
//       setDeleteing(false);
//     } catch (error) {
//       console.log(error);
//       setDeleteing(false);
//     }
//   };

//   const handleClose = () => {
//     setOpen(false);
//     setReasonForDelete("");
//   };

//   useEffect(() => {
//     setPage(0);
//   }, [bodyData]);

//   return (
//     <>
//       <TableContainer
//         component={Paper}
//         sx={{
//           border: "1px solid #d3d3d3", // Excel-like outer border
//           boxShadow: "none", // Remove shadow for a flatter look
//         }}
//       >
//         <Table
//           sx={{
//             borderCollapse: "collapse", // Ensure borders don't overlap
//             "& .MuiTableCell-root": {
//               border: "1px solid rgba(211, 211, 211, 0.27)", // Grid-like borders for all cells
//               padding: "8px", // Reduced padding for compact look
//               fontSize: "14px", // Consistent font size like Excel
//               fontFamily: "Calibri, Arial, sans-serif", // Excel-like font
//             },
//           }}
//         >
//           <TableHead
//             sx={{
//               backgroundColor: (theme) =>
//                 theme.palette.mode === "dark"
//                   ? theme.palette.primary.dark
//                   : theme.palette.primary.light,
//             }}
//           >
//             <TableRow>
//               <TableCell align="center">S.No</TableCell>
//               {tableKeys.map((r, i) => (
//                 <TableCell key={i} align="center">
//                   {r.name}
//                 </TableCell>
//               ))}
//               {formFields?.length
//                 ? formFields?.map((f, index) => (
//                     <TableCell
//                       key={f.inputLabel + index.toString()}
//                       align="center"
//                     >
//                       {f.inputLabel}
//                     </TableCell>
//                   ))
//                 : null}
//               {actions.length ? (
//                 <TableCell align="center">Action</TableCell>
//               ) : null}
//             </TableRow>
//           </TableHead>
//           <TableBody
//             sx={{
//               "& .MuiTableRow-root:hover": {
//                 backgroundColor: "#f5f5f5", // Light hover effect like Excel
//               },
//             }}
//           >
//             {bodyData.length && isDataReady
//               ? bodyData
//                   .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                   .map((data, index) => (
//                     <TableRow key={data._id}>
//                       <TableCell align="center">
//                         {page * rowsPerPage + index + 1}
//                       </TableCell>
//                       {tableKeys.map((k, i) => (
//                         <TableCell key={i} align="center">
//                           {getDataValue(data, k)}
//                         </TableCell>
//                       ))}
//                       {formFields?.length
//                         ? formFields?.map((f, index) => (
//                             <TableCell
//                               key={f.inputLabel + index.toString()}
//                               align="center"
//                             >
//                               {data.meta?.find(
//                                 (fi) => fi.inputLabel === f.inputLabel
//                               )?.value || ""}
//                             </TableCell>
//                           ))
//                         : null}
//                       <TableCell align="center">
//                         {actions.includes("edit") && (
//                           <ShowComponent module={module} action={"update"}>
//                             <Tooltip title="Edit">
//                               <IconButton onClick={() => onEditClick(data)}>
//                                 <EditIcon color="primary" fontSize="small" />
//                               </IconButton>
//                             </Tooltip>
//                           </ShowComponent>
//                         )}
//                         {actions.includes("view") && (
//                           <ShowComponent module={module} action={"view"}>
//                             <Tooltip title="View">
//                               <IconButton onClick={() => onViewClick(data)}>
//                                 <RemoveRedEyeRoundedIcon
//                                   fontSize="small"
//                                   color="primary"
//                                 />
//                               </IconButton>
//                             </Tooltip>
//                           </ShowComponent>
//                         )}
//                         {actions.includes("delete") && (
//                           <ShowComponent module={module} action={"delete"}>
//                             <Tooltip title="Delete">
//                               <IconButton
//                                 disabled={
//                                   deleteCanBeDisabled &&
//                                   !disableDeleteFunction(data)
//                                 }
//                                 onClick={() => handleDeleteClick(data)}
//                               >
//                                 <DeleteIcon
//                                   fontSize="small"
//                                   color={
//                                     deleteCanBeDisabled &&
//                                     !disableDeleteFunction(data)
//                                       ? "disabled"
//                                       : "error"
//                                   }
//                                 />
//                               </IconButton>
//                             </Tooltip>
//                           </ShowComponent>
//                         )}
//                         {actions.includes("download") && (
//                           <ShowComponent module={module} action={"view"}>
//                             <Tooltip title="Download">
//                               <IconButton
//                                 onClick={() => onDownload(data)}
//                                 disabled={!!downloadLoading[data._id]}
//                               >
//                                 {downloadLoading[data._id] ? (
//                                   <CircularProgress size={20} />
//                                 ) : (
//                                   <DownloadRoundedIcon fontSize="small" />
//                                 )}
//                               </IconButton>
//                             </Tooltip>
//                           </ShowComponent>
//                         )}
//                         {actions.includes("unbundle") && (
//                           <ShowComponent module={module} action={"delete"}>
//                             <Tooltip title="Unbundle">
//                               <Button
//                                 size="small"
//                                 variant="contained"
//                                 onClick={() => onUnBundleClick(data)}
//                               >
//                                 UnBundle
//                               </Button>
//                             </Tooltip>
//                           </ShowComponent>
//                         )}
//                         {actions.includes("switch") && (
//                           <ShowComponent module={module} action={"update"}>
//                             <Tooltip title="Active Status">
//                               <IconButton size="small">
//                                 <Switch
//                                   checked={data.active || false}
//                                   onChange={() => onToggleSwitch(data)}
//                                 />
//                               </IconButton>
//                             </Tooltip>
//                           </ShowComponent>
//                         )}
//                       </TableCell>
//                     </TableRow>
//                   ))
//               : null}
//           </TableBody>
//         </Table>
//         {!isDataReady ? (
//           <Box
//             sx={{
//               width: "100%",
//               display: "flex",
//               justifyContent: "center",
//               alignItems: "center",
//               paddingTop: "20px",
//             }}
//           >
//             <PuffLoader size={30} color="#14532d" />
//           </Box>
//         ) : null}
//         {isDataReady ? (
//           <NoTableData dataPresent={bodyData.length} title={bodyDataModal} />
//         ) : null}
//         <TablePagination
//           component="div"
//           count={bodyData ? (bodyData.length ? bodyData.length : 0) : 0}
//           page={page}
//           onPageChange={handleChangePage}
//           rowsPerPage={rowsPerPage}
//           onRowsPerPageChange={handleChangeRowsPerPage}
//           rowsPerPageOptions={ROWS_PER_PAGE}
//           sx={{
//             borderTop: "1px solid #d3d3d3", // Border above pagination
//             "& .MuiTablePagination-toolbar": {
//               fontFamily: "Calibri, Arial, sans-serif",
//               fontSize: "14px",
//             },
//           }}
//         />
//       </TableContainer>

//       {/* === Delete modal === */}
//       <Modal open={open} onClose={handleClose}>
//         <Box sx={style}>
//           <Box sx={{ textAlign: "center", margin: "10px auto" }}>
//             <img src={image} width={50} height={50} />
//           </Box>
//           <Typography
//             textAlign="center"
//             sx={{ fontSize: "18px", fontWeight: 700 }}
//           >
//             Delete Confirmation
//           </Typography>
//           <Typography sx={{ my: 2, textAlign: "center" }}>
//             Proceeding with the deletion of this data may result in information
//             inconsistency across other documents and is irreversible. Are you
//             certain you wish to delete this item?
//           </Typography>
//           <CustomInput
//             name="reason"
//             value={reasonForDelete}
//             onChange={(e) => setReasonForDelete(e.target.value)}
//             label="Reason For Deletion"
//             multiline
//             required={true}
//             rows={3}
//           />
//           <Box
//             mt={3}
//             sx={{
//               display: "flex",
//               justifyContent: "center",
//               alignItems: "center",
//               gap: "15px",
//             }}
//           >
//             <Button
//               size="small"
//               color="error"
//               variant="contained"
//               onClick={handleClose}
//             >
//               Cancel
//             </Button>
//             <LoadingButton
//               color="primary"
//               size="small"
//               variant="contained"
//               onClick={handleSubmit}
//               loading={deleting}
//             >
//               Delete
//             </LoadingButton>
//           </Box>
//         </Box>
//       </Modal>
//     </>
//   );
// }
